import _ from 'underscore';

import {assert} from 'Asserts';
import {takeContainedServerIdsFromClipboard} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {
  StructuredTableKind,
  UnknownStructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {isNoteLinkedFile} from 'notebook/TypePredicates';
import {Note, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import uniqueId from 'util/uniqueId';

import {EditorMixinInterface} from '../Editor';
import convertStructuredTableForPaste from '../structured-tables/convertStructuredTableForPaste';

/**
 * Reset the submitted state on each carried-over reaction row so a *copied* reaction table reads as
 * an editable, un-finalized copy rather than a submitted/synced one. We preserve the rows (components
 * + stoichiometry setup) and only clear `isFinalized` and `lastSyncedUpstreamValues` (the record of
 * the most recent submitted values). `lastSyncedUpstreamValues` is only cleared when present so we
 * never introduce the field on a row that never had it.
 *
 * Only call this for copy/clone paste paths. A same-document cut/paste is a *move* of the same logical
 * item — `convertStructuredTableForPaste` deliberately preserves its row/table UUIDs
 * (BNCH-151174) — so its finalized/synced state must be preserved too.
 *
 * Agent keep in sync: mirrors the backend clone in
 * `benchling/notebook/structured_tables/models/structured_table.py` (`StructuredTable.clone`, added in
 * BNCH-151755) for STOICHIOMETRY/LOOKUP kinds. Clone is only ever a copy, so it has no cut/paste case.
 */
function unfinalizeRowsForPaste(rows: Array<UnknownStructuredTableRow>): void {
  for (const row of rows) {
    row.isFinalized = false;
    if ('lastSyncedUpstreamValues' in row) {
      row.lastSyncedUpstreamValues = null;
    }
  }
}

export default function convertStoichiometryGroupForPaste(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any,
  name: string | null,
  component: Partial<EditorMixinInterface>,
  usedTableNames: Array<string>,
  destinationFileId: string,
  isConfig: boolean | undefined,
  isCutAndPasteWithinSameDocument: boolean,
  note?: Note
): void {
  assert(name != null, 'Stoichiometry group paste requires a name to avoid duplicate table name conflicts');

  // Clear the reaction link so a *copied* group is independent from the original reaction. A
  // same-document cut/paste is a move of the same logical group (its provenanceUuid and finalized
  // state are preserved), so it stays linked to the same reaction — leave groupMetadata intact.
  if (!isCutAndPasteWithinSameDocument) {
    item.item.groupMetadata = {reactionId: null};
  }

  // Server ids for the contained reaction-scheme file are smuggled through the clipboard by
  // NoteLinkedGroupPlugin so a cross-entry paste can resolve them (getIdFromCid only knows the
  // destination entry's cidIdMap). takeContainedServerIdsFromClipboard always strips the transient
  // field (so it never reaches the store or serialization) even when no scheme was smuggled.
  const containedServerIdsFromClipboard = takeContainedServerIdsFromClipboard(item);
  const newCidToServerId: CidIdMap = {};

  for (let i = 0; i < item.item.containedNoteParts.length; i++) {
    const containedNotePart = item.item.containedNoteParts[i];
    const containedItem = containedNotePart.item;
    if (component.props!.getType(containedItem) === NoteLinkedObjectType.STRUCTURED_TABLE) {
      assert(
        containedItem.kind === StructuredTableKind.STOICHIOMETRY,
        `Expected stoichiometry table but got ${containedItem.kind}`
      );
      const newContainedNotePart = convertStructuredTableForPaste(containedNotePart, {
        usedTableNames,
        destinationFileId,
        isDestinationConfig: isConfig,
        note,
      }).item;
      newContainedNotePart.item.name = name;
      // Preserve the reaction rows but un-finalize them so a *copied* table is editable and no longer
      // reads as submitted/synced against the original submission (mirrors the backend clone). A
      // same-document cut/paste is a move of the same logical item — convertStructuredTableForPaste
      // preserves its row/table UUIDs (BNCH-151174) — so leave its finalized/synced state intact.
      if (!isCutAndPasteWithinSameDocument) {
        unfinalizeRowsForPaste(newContainedNotePart.item.rows);
      }
      newContainedNotePart.cid = uniqueId('note-part');
      item.item.containedNoteParts[i] = newContainedNotePart;
    } else if (containedItem != null && isNoteLinkedFile(containedItem)) {
      // The reaction-scheme NoteLinkedFile's cid is a server-assigned id. Regenerate it and
      // re-register its server id (mirrors convertNotePartGridForPaste): same-entry paste resolves
      // via the shared cidIdMap; cross-entry paste falls back to the clipboard-embedded map. Reusing
      // the same server id (rather than cloning the file server-side) matches the image paste contract.
      const oldItemCid = containedItem.cid;
      const serverId =
        component.props!.getIdFromCid(oldItemCid) ?? containedServerIdsFromClipboard?.[oldItemCid];
      const newItemCid = uniqueId('note-linked-file-');
      containedItem.cid = newItemCid;
      containedNotePart.cid = uniqueId('note-part');
      if (serverId != null) {
        newCidToServerId[newItemCid] = serverId;
      }
    }
  }

  if (!_.isEmpty(newCidToServerId)) {
    component.props!.registerObjects(newCidToServerId);
  }
}
